import angular from 'angular';
import fundItemTemplate from './fundItems.html';

/*****************************************************************************
 * Component: fundItems */
/**
 * @ngdoc     component
 * @module    csm.books.fundraisers
 * @name      csm.books.fundraisers.component:fundItems
 * @description
 * Component definition object for {@link fundItems}. The component is used to
 * manage grid content for the `items` component which is used for the Item Costs,
 * Fixed Items and Item Sales tabs. This component requires the `items` component
 * as its parent.
 *****************************************************************************/
export const fundItemsComponent = {
  require: { itemsCtrl: '^items' },
  controller: FundItemsController,
  template: fundItemTemplate,
  bindings: { gridData: '<' }
};

/*****************************************************************************
 * Controller: FundItemsController */
/**
 * @ngdoc     controller
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.items.controller:FundItemsController
 * @description
 * Main controller for {@link fundItems}.
 *****************************************************************************/
/* @ngInject */ function FundItemsController($injector) {
  var $ctrl = this;
  $ctrl.dgFundItems = $injector.invoke(buildGridOptions, null, {
    $ctrl: $ctrl
  });
  $ctrl.changeFundItem = $injector.invoke(changeFundItem, null, {
    $ctrl: $ctrl
  });
  $ctrl.deleteFundItem = $injector.invoke(deleteFundItem, null, {
    $ctrl: $ctrl
  });
  $ctrl.$onChanges = $injector.invoke($onChanges, null, { $ctrl: $ctrl });

  /*---------------------*/
  /* Grid API reference. */
  /*---------------------*/
  $ctrl.gridApi;
}

/*****************************************************************************
 * buildGridOptions */
/**
 * @ngdoc    function
 * @name     buildGridOptions
 * @methodOf csm.books.fundraisers.items.controller:FundItemsController
 * @returns  {object} uiGrid definition object
 * @description
 * Builds and returns the grid definition object.
 *****************************************************************************/
/* @ngInject */ function buildGridOptions(uiGridConstants, $ctrl) {
  /*----------------------------------*/
  /* Template for the actions column. */
  /*----------------------------------*/
  var actionsTemplate =
    '<div class="ui-grid-cell-contents ng-binding ng-scope"> ' +
    '<table><tr>' +
    '<td style="width:25px;"><span style="cursor:pointer;" class="glyphicon glyphicon-edit  csm-grid-space-target" title="Change Fundraiser Item" ng-click="grid.appScope.$ctrl.changeFundItem(row.entity)"></span></td>' +
    '<td style="width:25px;"><span style="cursor:pointer;" class="glyphicon glyphicon-trash csm-grid-space-target" title="Delete Fundraiser Item" ng-click="grid.appScope.$ctrl.deleteFundItem(row.entity.id)"></span></td>' +
    '</tr></table>' +
    '</div>';

  /*------------------------------------------------------------------*/
  /* Row template to add double click functionality to each grid row. */
  /*------------------------------------------------------------------*/
  var dblClickTemplate =
    '<div ng-dblclick="grid.appScope.$ctrl.changeFundItem(row.entity)" \
          ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" \
              class="ui-grid-cell" ui-grid-cell> \
    </div>';

  return {
    enableColumnMenus: false,
    enableColumnResizing: true,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    minRowsToShow: 8,
    maxRowsToShow: 8,
    rowTemplate: dblClickTemplate,
    onRegisterApi: function (api) {
      $ctrl.gridApi = api;
    },
    columnDefs: [
      {
        displayName: 'Name',
        field: 'itemName',
        type: 'string',
        width: 460
      },
      {
        displayName: 'Quantity',
        field: 'qty',
        type: 'number',
        width: 100
      },
      {
        cellClass: 'text-right',
        cellFilter: 'number:2',
        field: 'amount',
        type: 'number',
        width: 100
      },
      {
        cellClass: 'text-right',
        cellFilter: 'number:2',
        field: 'subtotal',
        type: 'number',
        width: 100
      },
      {
        cellTemplate: actionsTemplate,
        enableColumnResizing: false,
        displayName: 'Actions',
        enableFiltering: false,
        enableSorting: false,
        field: 'actions',
        pinnedRight: true,
        minWidth: 90,
        width: 90
      }
    ]
  };
}

/*****************************************************************************
 * changeFundItem */
/**
 * @ngdoc    function
 * @name     changeFundItem
 * @methodOf csm.books.fundraisers.items.controller:FundItemsController
 * @param    {Object}  Fundraiser Item to modify.
 * @returns  {Promise} Promise that resolves when the dialog closes.
 * @description
 * Displays the `AddChangeFundraiserItemDialog` to modify a Fundraiser Item.
 *****************************************************************************/
/* @ngInject */ function changeFundItem(
  AddChangeFundraiserItemDialog,
  Alert,
  $ctrl
) {
  return function (fundItem) {
    AddChangeFundraiserItemDialog.show(
      false,
      angular.copy(fundItem),
      $ctrl.itemsCtrl.dialogCtrl.dpItemCostsNotSold
    ).then(function (item) {
      /*----------------------------------------*/
      /* Update the grid with the updated item. */
      /*----------------------------------------*/
      var idx = $ctrl.dgFundItems.data
        .map(function (fi) {
          return fi.id;
        })
        .indexOf(item.id);
      $ctrl.dgFundItems.data[idx] = item;
      Alert.success(' ', 'Item Updated');
    });
  };
}

/*****************************************************************************
 * deleteFundItem */
/**
 * @ngdoc    function
 * @name     deleteFundItem
 * @methodOf csm.books.fundraisers.items.controller:FundItemsController
 * @param    {Integer}  ID for Fundraiser Item to delete.
 * @returns  {Promise}  Promise that resolves when the dialog closes.
 * @description
 * Deletes the Fundraiser Item.
 *****************************************************************************/
/* @ngInject */ function deleteFundItem(Alert, BusyScreen, FundRaiser, $ctrl) {
  return function (id) {
    /*--------------------------*/
    /* Nothing to delete, exit. */
    /*--------------------------*/
    if (!id) return;

    /*---------------------*/
    /* Confirm and remove. */
    /*---------------------*/
    Alert.confirm(
      'Are you sure you want to delete this item?',
      'Confirm Remove'
    ).then(function () {
      BusyScreen.setBusy(true);
      FundRaiser.deleteFundItem(id).then(
        function () {
          /*------------------------------------------------------*/
          /* Remove the fund item that was deleted from the grid. */
          /*------------------------------------------------------*/
          var idx = $ctrl.dgFundItems.data
            .map(function (fi) {
              return fi.id;
            })
            .indexOf(id);
          $ctrl.dgFundItems.data.splice(idx, 1);

          Alert.success(' ', 'Item Deleted');
          BusyScreen.setBusy(false);
        },
        function (resp) {
          Alert.error(resp.parsedResponse.message(), 'Delete Failed');
        }
      );
    });
  };
}

/*****************************************************************************
 * $onChanges */
/**
 * @ngdoc    function
 * @name     $onChanges
 * @methodOf csm.books.fundraisers.items.controller:FundItemsController
 * @param    {Integer}  ID for Fundraiser Item to delete.
 * @returns  {Promise}  Promise that resolves when the dialog closes.
 * @description
 * This will be called automatically by angular when any of the bound properties
 * changes. Update the grid when the reference to the bound dataprovider has
 * been changed.
 *****************************************************************************/
/* @ngInject */ function $onChanges($ctrl) {
  return function (changes) {
    if (changes.gridData) {
      $ctrl.dgFundItems.data = changes.gridData.currentValue;
      $ctrl.gridApi ? $ctrl.gridApi.core.refresh() : angular.noop;
    }
  };
}
